import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Smartphone, Mail, KeyRound, RefreshCw, CheckCircle, ShieldCheck } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import RetailCliqLogo from '../components/RetailCliqLogo';
import ThemeToggle from '../components/ThemeToggle';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import FloatingParticles from '../components/FloatingParticles';
import { API_BASE, API } from '../../lib/api';

const OTP_TOTAL = 60;

// Custom OTP input component
function OTPInputGroup({ value = '', onChange, disabled = false }) {
  const inputRefs = useRef(Array.from({ length: 6 }, () => React.createRef()));

  const handleChange = (index, val) => {
    const digits = (value || '').split('');
    digits[index] = val.replace(/\D/g, '').slice(-1);
    const result = digits.join('').slice(0, 6);
    onChange(result);
    // Auto-advance
    if (val && index < 5) {
      inputRefs.current[index + 1]?.current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        inputRefs.current[index - 1]?.current?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.current?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.current?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) {
      onChange(pasted.padEnd(6, '').slice(0, 6));
      inputRefs.current[Math.min(pasted.length, 5)]?.current?.focus();
    }
    e.preventDefault();
  };

  return (
    <div
      className="flex items-center gap-2.5"
      data-testid="mfa-otp-input"
    >
      {Array.from({ length: 6 }, (_, i) => (
        <input
          key={i}
          ref={inputRefs.current[i]}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          aria-label={`OTP digit ${i + 1}`}
          className={`
            w-12 h-14 text-center text-xl font-bold rounded-xl border-2 outline-none
            transition-colors duration-150
            dark:bg-white/[0.06] bg-white
            dark:text-white text-gray-900
            ${value[i]
              ? 'dark:border-[#3DD56D] border-[#3DD56D] dark:shadow-[0_0_12px_rgba(61,213,109,0.2)] shadow-[0_0_12px_rgba(61,213,109,0.15)]'
              : 'dark:border-white/[0.15] border-gray-200'
            }
            focus:dark:border-[#3DD56D] focus:border-[#3DD56D]
            focus:dark:shadow-[0_0_14px_rgba(61,213,109,0.25)] focus:shadow-[0_0_14px_rgba(61,213,109,0.15)]
            disabled:opacity-40 disabled:cursor-not-allowed
          `}
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        />
      ))}
    </div>
  );
}

// Countdown SVG ring
function CountdownRing({ seconds, total = OTP_TOTAL }) {
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, seconds) / total;
  const offset = circumference * (1 - progress);
  const color =
    seconds > 30 ? '#3DD56D' :
    seconds > 15 ? '#F5C451' :
    '#FF5A6A';

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="88" height="88" viewBox="0 0 88 88" className="-rotate-90">
        {/* Track */}
        <circle
          cx="44" cy="44" r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="4"
        />
        {/* Progress */}
        <circle
          cx="44" cy="44" r={radius}
          fill="none"
          stroke={color}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.5s ease' }}
        />
      </svg>
      {/* Number */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="text-2xl font-bold tabular-nums"
          style={{ color, fontFamily: "'IBM Plex Mono', monospace" }}
          data-testid="mfa-countdown"
        >
          {String(Math.max(0, seconds)).padStart(2, '0')}
        </span>
      </div>
    </div>
  );
}

const MFA_METHODS = [
  { id: 'sms', label: 'SMS', icon: Smartphone },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'app', label: 'Authenticator', icon: KeyRound },
];

export default function MFAPage() {
  const { tempToken, emailHint, userSummary, rememberMe, completeLogin } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [otp, setOtp] = useState('');
  const [mfaMethod, setMfaMethod] = useState('email');
  const [seconds, setSeconds] = useState(OTP_TOTAL);
  const [canResend, setCanResend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (seconds <= 0) { setCanResend(true); return; }
    const t = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  const handleResend = () => {
    setOtp('');
    setSeconds(OTP_TOTAL);
    setCanResend(false);
    toast.success('OTP resent successfully!');
  };

  const handleVerify = useCallback(async () => {
    if (otp.length < 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-mfa`, {
        temp_token: tempToken,
        otp,
        method: mfaMethod,
      });
      setVerified(true);
      completeLogin(data.access_token, data.user_summary, rememberMe);
      setTimeout(() => navigate('/select-app'), 800);
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Invalid OTP. Please try again.');
      setOtp('');
    } finally {
      setLoading(false);
    }
  }, [otp, tempToken, mfaMethod, completeLogin, rememberMe, navigate]);

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (otp.length === 6 && !loading && !verified) {
      handleVerify();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const maskedContact = mfaMethod === 'sms'
    ? '+91 ****\u2022\u2022\u2022\u22c51234'
    : mfaMethod === 'email'
    ? (emailHint || 'your registered email')
    : 'Authenticator App';

  return (
    <div className="min-h-screen dark:bg-[#080808] bg-slate-50 flex flex-col relative overflow-hidden">
      {isDark && <FloatingParticles />}
      {!isDark && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(800px circle at 50% 30%, rgba(61,213,109,0.05) 0%, transparent 60%)' }}
        />
      )}

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-6 py-5 dark:border-b dark:border-white/[0.06] border-b border-gray-100">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/login')}
            className="w-9 h-9 rounded-xl dark:bg-white/[0.05] bg-white dark:border-white/[0.09] border-gray-200 border flex items-center justify-center dark:text-neutral-400 text-gray-500 dark:hover:text-white hover:text-gray-900 dark:hover:bg-white/[0.1] hover:bg-gray-100 transition-colors"
            aria-label="Back to login"
          >
            <ArrowLeft size={16} />
          </button>
          <RetailCliqLogo size="sm" />
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-[#3DD56D]/10 text-[#3DD56D] border-[#3DD56D]/25 border hidden sm:flex">
            <ShieldCheck size={12} className="mr-1" /> Secure Session
          </Badge>
          <ThemeToggle />
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.38, ease: 'easeOut' }}
          className="w-full max-w-md"
        >
          {!verified ? (
            <div className="dark:bg-white/[0.04] bg-white dark:border-white/[0.09] border-gray-200 border dark:shadow-[0_24px_60px_rgba(0,0,0,0.55)] shadow-xl rounded-2xl p-8">

              {/* Header */}
              <div className="text-center mb-8">
                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-[#3DD56D]/[0.08] border border-[#3DD56D]/20 mx-auto mb-4">
                  <ShieldCheck size={28} className="text-[#3DD56D]" />
                </div>
                <h2
                  className="text-2xl font-bold dark:text-white text-gray-900"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  Verify Your Identity
                </h2>
                {userSummary && (
                  <p className="dark:text-neutral-400 text-gray-500 text-sm mt-1.5">
                    Welcome back, <span className="dark:text-white text-gray-900 font-medium">{userSummary.name}</span>
                  </p>
                )}
                <p className="dark:text-neutral-500 text-gray-400 text-xs mt-1">
                  Verification code sent to <span className="dark:text-neutral-300 text-gray-600">{maskedContact}</span>
                </p>
              </div>

              {/* Method selector */}
              <div className="mb-6">
                <Tabs
                  value={mfaMethod}
                  onValueChange={v => { setMfaMethod(v); setOtp(''); }}
                  data-testid="mfa-method-select"
                >
                  <TabsList className="w-full dark:bg-white/[0.05] bg-gray-100 p-1 rounded-xl h-auto">
                    {MFA_METHODS.map(({ id, label, icon: Icon }) => (
                      <TabsTrigger
                        key={id}
                        value={id}
                        className="flex-1 flex items-center gap-1.5 text-xs py-2 rounded-lg dark:data-[state=active]:bg-white/[0.12] data-[state=active]:bg-white dark:data-[state=active]:text-white data-[state=active]:text-gray-900 dark:text-neutral-400 text-gray-500"
                      >
                        <Icon size={13} /> {label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>

              {/* OTP + countdown */}
              <div className="flex flex-col items-center gap-6 mb-8">
                {/* Countdown ring */}
                <div className="relative">
                  <CountdownRing seconds={seconds} />
                  {canResend && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs text-[#FF5A6A] font-medium">Expired</span>
                    </div>
                  )}
                </div>

                {/* OTP boxes */}
                <OTPInputGroup
                  value={otp}
                  onChange={setOtp}
                  disabled={loading || canResend}
                />
              </div>

              {/* Email OTP hint */}
              <div className="dark:bg-blue-500/[0.06] bg-blue-50 border dark:border-blue-500/20 border-blue-200 rounded-xl px-4 py-3 mb-5 text-center">
                <p className="text-xs dark:text-neutral-400 text-gray-500">
                  Check your registered email inbox for the OTP code
                </p>
              </div>

              {/* Action buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handleVerify}
                  data-testid="mfa-verify-button"
                  disabled={loading || otp.length < 6 || canResend}
                  className="w-full h-11 rounded-xl bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold shadow-[0_6px_20px_rgba(61,213,109,0.22)] disabled:opacity-40"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                      Verifying...
                    </span>
                  ) : 'Verify OTP'}
                </Button>

                <Button
                  variant="ghost"
                  onClick={handleResend}
                  data-testid="mfa-resend-button"
                  disabled={!canResend}
                  className="w-full h-10 rounded-xl dark:text-neutral-400 text-gray-500 dark:hover:text-white hover:text-gray-900 dark:hover:bg-white/[0.05] disabled:opacity-30"
                >
                  <RefreshCw size={14} className="mr-2" />
                  Resend OTP
                </Button>
              </div>

              <p className="text-center dark:text-neutral-600 text-gray-400 text-xs mt-5">
                Having trouble?{' '}
                <a href="/login" className="text-[#3DD56D] hover:text-[#5fe88a]">
                  Go back to login
                </a>
              </p>
            </div>
          ) : (
            /* Success state */
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="dark:bg-white/[0.04] bg-white dark:border-white/[0.09] border-gray-200 border rounded-2xl p-10 text-center"
            >
              <div className="w-20 h-20 rounded-full bg-[#3DD56D]/10 border-2 border-[#3DD56D]/30 flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={36} className="text-[#3DD56D]" />
              </div>
              <h3 className="text-xl font-bold dark:text-white text-gray-900 mb-1.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Verified!
              </h3>
              <p className="dark:text-neutral-400 text-gray-500 text-sm">Redirecting to your applications...</p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
